// What clicking an OPEN ENTITY'S attachment does (busy-my-web#396; the rule below re-stated
// 2026-09-15 for the parity audit's D-4, busy-my-web#481 — see "WHERE THIS RULE STOPS").
//
// Saffet: "When I want to view a photo that is in my task it asks me to download to my computer and
// i cannot cancel it also". Every chip in that group downloaded, by a recorded decision — "exactly
// as the attachment chips elsewhere do (EntityAttachments)" — because a stored document has no
// public URL to preview from, so handing one to the viewer opened an empty frame.
//
// That reasoning holds for a spreadsheet and fails for a picture: the bytes can be fetched and
// turned into a blob URL, which the viewer shows exactly as it shows a research photo, with its own
// Download button beside the zoom. So the rule splits in two, and it splits HERE rather than inside
// the component so both halves can be tested without a network:
//
//   • a picture   → fetch once, open in the middle preview. Nothing lands on the computer until
//                   the Download button in the viewer is pressed.
//   • anything else → the download it has always been.
//
// ── WHERE THIS RULE STOPS (2026-09-15 parity audit D-4, busy-my-web#481) ───────────────────────
//
// #396's "anything else → the download" was written about ONE surface: the chips under an entity
// open in the box, drawn by the Work-board pane, where a PDF has nowhere to be drawn. That is still
// true and that half is unchanged — `opensInPreview` below still answers "is it a picture" for
// those chips and nothing else.
//
// It was never a rule about the MAIL READER, and the audit found it being read as one: a PDF on an
// open email had no way to be looked at either. It does now — components/Email/AttachmentViewer.vue
// renders PDFs, text, CSV and documents in the middle of the Email Workspace, with the file's own
// toolbar in the box and its card on the rail — so from the reader the answer to "anything else" is
// no longer a download, it is that viewer (`readerOpensViewer`).
//
// The split is by SURFACE, not by file type, and it is deliberate: an entity's chip has no
// workspace middle to open into (it is drawn inside the box, on any page), while a message being
// read in the Email Workspace does. Nothing about Saffet's report is reversed — a picture still
// previews, and an entity chip with nowhere to open still downloads.

import { mediaKindOf, mediaMimeOf } from '@/lib/mediaPlayer'

/**
 * Does this attachment open in the in-app preview rather than downloading?
 *
 * Reads `isImage`, which lib/briefings/entityFiles.js has already worked out from the document's
 * content type and its file name — one answer to "is this a picture", not a second opinion.
 *
 * @param {object} file a card from `fileCards`
 * @returns {boolean}
 */
export function opensInPreview(file) {
  return previewKindOf(file) !== null
}

// ── …AND A PDF OR A TEXT FILE PREVIEWS TOO (Pouria, 2026-10-02) ───────────────────────────────
//   "we need have preview for items on task and note … make sure we can preview the attachment
//    on task"
// The reasoning above ("a PDF has nowhere to be drawn") stopped being true for the entity chips:
// MiddlePreview's `kind: 'file'` already draws a PDF from a blob URL in an iframe (the browser's own
// viewer, with its own download), and a plain-text file the same way. So a chip, a reader card or a
// rail line now OPENS a picture, a PDF or a text file in the middle, and only what a browser cannot
// draw (a spreadsheet, a .docx) still downloads.

// ── …AND A VIDEO OR AN AUDIO FILE OPENS TOO (busy-my-web#639, 2026-10-20) ─────────────────────
// The full-window viewer's "video view" and "Audio view" frames (Figma 40014356:382673 /
// 40014356:382752) draw a stored video and a stored audio file with their own players
// (SmartBox/FileViewerVideo.vue / FileViewerAudio.vue), so those leave the "downloads" half of
// the rule too: a video or audio file is a 'file' preview, briefed by the box like a PDF, and its
// "Open" plays it in the viewer. Which files count is lib/mediaPlayer `mediaKindOf` (content type
// first, extension second). A spreadsheet, a .docx, an archive still download.

const PDF_TYPE = 'application/pdf'
const extOf = (name) => {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || ''))
  return m ? m[1].toLowerCase() : ''
}
const TEXT_EXT = ['txt', 'csv', 'md', 'log', 'json']

/**
 * The content type a file's bytes should be wrapped in — its own, or one read off its extension
 * when the store kept none (an iframe will not render a PDF served as octet-stream).
 */
export function previewContentType(file) {
  const ct = String(file?.contentType || '').toLowerCase()
  if (ct && ct !== 'application/octet-stream') return ct
  const ext = extOf(file?.name || file?.fileName || file?.filename)
  if (ext === 'pdf') return PDF_TYPE
  if (TEXT_EXT.includes(ext)) return ext === 'json' ? 'application/json' : 'text/plain'
  // A video / audio file kept as octet-stream: a <video> sniffs most containers anyway, but a
  // typed blob is what makes Safari and the waveform's decodeAudioData take it (#639).
  const media = mediaMimeOf(file?.name || file?.fileName || file?.filename)
  if (media) return media
  return ct || 'application/octet-stream'
}

/**
 * How a file opens in the middle: 'photo' for a picture, 'file' for a PDF, a text file, a video or
 * an audio file (#639), null for anything the browser cannot draw (that one downloads).
 */
export function previewKindOf(file) {
  if (!file) return null
  if (file.isImage === true) return 'photo'
  const ct = previewContentType(file)
  if (/^image\//.test(ct)) return 'photo'
  if (ct === PDF_TYPE || /^text\//.test(ct) || ct === 'application/json') return 'file'
  if (mediaKindOf(ct, file.name || file.fileName || file.filename)) return 'file'
  return null
}

/**
 * The item to hand `workboard/openPreview` for a file attached to the open entity.
 *
 * `kind: 'photo'` is what makes MiddlePreview draw it as an image (zoom, Download, close);
 * `kind: 'file'` with a PDF / text `contentType` and the blob URL draws it in the browser's own
 * viewer. The document's own id is kept so the viewer's Keep/Remove pair addresses the same thing
 * the chip does.
 *
 * @param {object} file a card from `fileCards` / `readerAttachmentCards`, or a rail line's `doc`
 * @param {string} url  the object URL the fetched bytes were turned into
 * @returns {object|null} the preview item, or null when there is nothing to show
 */
export function attachmentPreviewItem(file, url) {
  const kind = previewKindOf(file)
  if (!kind || !url) return null
  return {
    id: file.id,
    kind,
    title: file.name || file.fileName || 'Attachment',
    contentType: previewContentType(file),
    url,
    // The STORED DOCUMENT behind it (Figma frames 60–63, Pouria 2026-10-15): an item carrying a
    // `documentId` is briefed by the box (lib/briefings/file.js `isFileBriefing`,
    // store/modules/fileBriefing.js) rather than thrown over the middle — the file briefing with
    // its own "Open" into the full-window viewer. Every road that opens a stored document passes
    // here, so this one field is what turns all of them into that state.
    documentId: String(file.id),
  }
}

/**
 * Does a chip in the MAIL READER open the middle viewer, rather than previewing inline?
 *
 * Only where there is a middle to open into. The reader is mounted in two places: inside the Email
 * Workspace (busy-my-web#322), where the session owns the middle and the viewer is a mode of it,
 * and on /emails/timeline/:id, which is a page of its own with no workspace around it. Opening the
 * session's viewer from the second would set a record nothing is drawing — the file would simply
 * not appear — so there the inline preview under the message stays exactly as it was.
 *
 * @param {{sessionActive?:boolean}} where
 * @returns {boolean}
 */
export function readerOpensViewer({ sessionActive = false } = {}) {
  return !!sessionActive
}

// ── OPENING A STORED DOCUMENT FROM A TIMELINE ROW (doc-attach, 2026-10-02) ─────────────────────
//
// The story's "Attachment added" steps and the entity rail's attachment lines open the same thing
// the Work-board's chips do: a document filed in Busy, read back through the AUTHENTICATED
// `document/download/{id}` route. A private upload has no public link at all (`webContentLink` is
// null by decision — owner-only), so the bytes are the only way in. Kept here, beside the rule that
// splits picture from everything else, and written over injected `api` / `doc` so the whole road
// is testable without a network or a page.

/**
 * Fetch a document's bytes as an object URL. The caller owns the URL (revoke it when done).
 *
 * @param {object} api the general service (`downloadDocument`)
 * @param {{id:string, contentType?:string}} file
 * @returns {Promise<string>} the object URL
 */
export async function documentObjectUrl(api, file) {
  const res = await api.downloadDocument(file.id)
  const blob = new Blob([res.data], { type: previewContentType(file) })
  return URL.createObjectURL(blob)
}

/**
 * Save an object URL to the computer under `name` — the anchor-click every download in the app uses.
 *
 * @param {string} url
 * @param {string} name
 * @param {Document} [doc] injectable for tests
 */
export function saveObjectUrl(url, name, doc = (typeof document !== 'undefined' ? document : null)) {
  if (!url || !doc) return
  const a = doc.createElement('a')
  a.href = url
  a.download = name || 'attachment'
  doc.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * Download a stored document, whatever it is.
 *
 * @param {object} api
 * @param {{id:string, name?:string, contentType?:string}} file
 */
export async function downloadStoredDocument(api, file) {
  if (!file?.id) return
  const url = await documentObjectUrl(api, file)
  saveObjectUrl(url, file.name || file.fileName)
  URL.revokeObjectURL(url)
}

/**
 * OPEN a stored document: a picture, a PDF, a text file, a video or an audio file goes to `preview(item)` (the middle preview — the caller keeps
 * the URL alive for as long as it is shown), anything else is downloaded. Resolves to the preview
 * item when one was shown, else null.
 *
 * @param {object} api
 * @param {{id:string, name?:string, contentType?:string, isImage?:boolean}} file — `isImage` as
 *   lib/briefings/entityFiles worked it out
 * @param {{preview?: function}} [opts]
 */
export async function openStoredDocument(api, file, { preview } = {}) {
  if (!file?.id) return null
  const url = await documentObjectUrl(api, file)
  const item = typeof preview === 'function' ? attachmentPreviewItem(file, url) : null
  if (item) {
    preview(item)
    return item
  }
  saveObjectUrl(url, file.name || file.fileName)
  URL.revokeObjectURL(url)
  return null
}

// ── OPENING A WORK-BOARD FILE CHIP (busy-my-web#584) ─────────────────────────────────────────────
//
// Saffet: "When I add a photo to a task I cannot open it in the work-Board to view it." The chip
// used to open a Drive-uploaded file through its `webContentLink` — but a PRIVATE upload has none
// (see above), so once the photo had gone up with the save the viewer was handed `url: null` and
// drew an empty <img>. And the authenticated route cannot rescue it by the chip's `fileId`:
// `document/download/{id}` is keyed by the Busy DOCUMENT id, a Guid, and `fileId` is the Drive id.
//
// So the chip's bytes come, in order, from:
//   1. 'local'    — the computer File the chip was staged from, kept on the chip after the upload
//                   (SmartBox `stagedComputerFile`): no network at all, uploaded or not;
//   2. 'link'     — a public link a legacy / email-path upload still carries;
//   3. 'document' — the stored document behind a "Recent saved files" pick (`recentId` is its
//                   Busy id, lib/recentSavedFiles `recentRow`), fetched through the route above;
//   4. 'none'     — nothing to fetch from (a Drive-picker pick has only its Drive id): the viewer
//                   shows the file's name rather than a blank picture.

/**
 * Where a Work-board file chip's bytes come from — see the list above.
 *
 * @param {{file?:Blob, webContentLink?:string, recentId?:string, documentId?:string}} chip
 * @returns {{from:'local'|'link'|'document'|'none', url?:string, documentId?:string}}
 */
export function chipFileSource(chip) {
  if (!chip) return { from: 'none' }
  if (chip.file) return { from: 'local' }
  if (chip.webContentLink) return { from: 'link', url: chip.webContentLink }
  const documentId = String(chip.documentId || chip.recentId || '')
  if (documentId) return { from: 'document', documentId }
  return { from: 'none' }
}
